import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BorderOptions from "%/dynamic-component-editor/bundles/border-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import { isRatioValue, aspectForRatio } from "%/dynamic-component-editor/options/aspect-ratios";
import ObjectFit from "%/dynamic-component-editor/options/object-fit";
import ImageAltText from "%/dynamic-component-editor/options/image-alt-text";
import ImageShape from "%/dynamic-component-editor/options/image-shape";
import ObjectPosition from "%/dynamic-component-editor/options/object-position";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import ImageFillBackground from "%/dynamic-component-editor/options/image-fill-background";
import BorderSides from "%/dynamic-component-editor/options/border-sides";
import Padding from "%/dynamic-component-editor/options/padding";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import Margins from "%/dynamic-component-editor/options/margins";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MinHeight from "%/dynamic-component-editor/options/min-height";
import OpenInNewTab from "%/dynamic-component-editor/options/open-in-new-tab";
import LinkAction from "%/dynamic-component-editor/options/link-action";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import HrefOption from "%/dynamic-component-editor/options/href-option";
import Crop from "%/dynamic-component-editor/options/crop";
import Label from "%/dynamic-component-editor/options/label";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import BackgroundGradient from "%/dynamic-component-editor/options/background-gradient";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import BoxShadow from "%/dynamic-component-editor/options/box-shadow";
import Blur from "%/dynamic-component-editor/options/blur";
import Brightness from "%/dynamic-component-editor/options/brightness";
import Contrast from "%/dynamic-component-editor/options/contrast";
import Grayscale from "%/dynamic-component-editor/options/grayscale";
import HueRotate from "%/dynamic-component-editor/options/hue-rotate";
import Invert from "%/dynamic-component-editor/options/invert";
import ImageOpacity from "%/dynamic-component-editor/options/opacity";
import Saturate from "%/dynamic-component-editor/options/saturate";
import Sepia from "%/dynamic-component-editor/options/sepia";
import BlendMode from "%/dynamic-component-editor/options/blend-mode";
import Rendering from "%/dynamic-component-editor/options/rendering";
import ClipPath from "%/dynamic-component-editor/options/clip-path";
import AnimationEffectsOptions from "%/dynamic-component-editor/bundles/animation-effects-options";

const IMAGE_APPEARANCE_OPTIONS = new AppearanceOptions({}, {
  "border-radius": false,
  "blur": true,
  "opacity": true,
});
const IMAGE_EFFECTS_OPTIONS = new EffectsOptions({}, {
  "text-shadow": true,
});
const IMAGE_BACKGROUND_OPTIONS = new BackgroundOptions({}, {
  "background-image": true,
});

export const IMAGE_STYLE_OPTION_BUNDLES = Object.freeze([
  IMAGE_APPEARANCE_OPTIONS,
  AnimationEffectsOptions,
  BorderOptions,
  IMAGE_EFFECTS_OPTIONS,
  IMAGE_BACKGROUND_OPTIONS,
]);

export const StandardImageElementMixin = {
  extends: DynamicElementMixin,
  supportsRatioSize: true,
  mixins: [GestureActionsMixin],
  gestureOption: LinkAction,
  elementPanelSettings: {
    displayIcon: "image",
    elementType: "element",
  },
  inject: {
    inBuilderEditScope: {
      from: "inBuilderEditScope",
      default: false,
    },
  },
  optionBundles: [
    new LayoutWidthHeightOptions({
      "default.layout-width": 300,
    }),
    IMAGE_APPEARANCE_OPTIONS,
    AnimationEffectsOptions,
    BorderOptions,
    SpacingOptions,
    IMAGE_EFFECTS_OPTIONS,
    IMAGE_BACKGROUND_OPTIONS,
  ],
  options: [
    LinkAction,
    new HrefOption(undefined, true),
    new Label(undefined, true),
    new LinkType(undefined, true),
    new OpenInNewTab(undefined, true),
  ],
  data() {
    return {
      LayoutWidth,
      LayoutHeight,
      ObjectFit,
      ImageShape,
      ObjectPosition,
      BorderRadius,
      ImageFillBackground,
      BorderOptions,
      MinHeight,
      MinWidth,
      MaxHeight,
      MaxWidth,
      Crop,
      BackgroundColor,
      BackgroundGradient,
      BlendMode,
      Rendering,
    };
  },
  computed: {
    builderNoNavHref() {
      if (this.editMode || this.inBuilderEditScope || this.gestureOwnsTheClick) {
        return null;
      }
      return this.imageHref;
    },
    target() {
      return this.getOptionValue(LinkAction)?.openInNewTab ? "_blank" : "_self";
    },
    fallbackImageSrc() {
      throw new DynamicComponentError("Element must implement fallbackImageSrc computed property when extending base image element");
    },
    entityPageHref() {
      return null;
    },
    imageHref() {
      if (this.entityPageHref) {
        return this.entityPageHref;
      }
      const linkValues = this.getOptionValue(LinkAction);
      if (!linkValues) {
        return "";
      }
      const urlType = linkValues.linkType;
      let urlValue = linkValues.value;

      switch (urlType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE:
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          return `${this.links.web}/${urlValue}`;
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            urlType,
            urlValue,
          });
      }
    },
    imageSrc() {
      throw new DynamicComponentError("Element must implement imageSrc computed property when extending base image element");
    },
    imageMediaUnresolvable() {
      return false;
    },
    imageAltText() {
      return this.getOptionValue(ImageAltText) || "";
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    myClipPath() {
      return this.getOptionValue(ClipPath);
    },
    myHeight() {
      const heightValue = this.getOptionValue(LayoutHeight);
      if (heightValue === "hug" || isRatioValue(heightValue)) {
        return "auto";
      }
      return heightValue;
    },
    myWidth() {
      const widthValue = this.getOptionValue(LayoutWidth);
      if (widthValue === "hug" || isRatioValue(widthValue)) {
        return "auto";
      }
      return widthValue;
    },
    isRatioActive() {
      return isRatioValue(this.getOptionValue(LayoutWidth)) || isRatioValue(this.getOptionValue(LayoutHeight));
    },
    myImageAspectRatio() {
      const widthValue = this.getOptionValue(LayoutWidth);
      if (isRatioValue(widthValue)) {
        return aspectForRatio(widthValue);
      }
      const heightValue = this.getOptionValue(LayoutHeight);
      if (isRatioValue(heightValue)) {
        return aspectForRatio(heightValue);
      }
      return null;
    },
    myCropIfNeeded() {
      return this.isRatioActive ? true : this.getOptionValue(Crop);
    },
    imageStyleOverrides() {
      return {
        borderRadius: this.myBorderRadius,
        paddingTop: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "top") || "none",
        paddingBottom: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "bottom") || "none",
        paddingLeft: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "left") || "none",
        paddingRight: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "right") || "none",
        marginTop: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "top") || "none",
        marginBottom: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "bottom") || "none",
        marginLeft: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "left") || "none",
        marginRight: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "right") || "none",
        paddingHoverTop: this.getOptionValue(Padding, BaseOption.$states.HOVER, "top") || "none",
        paddingHoverBottom: this.getOptionValue(Padding, BaseOption.$states.HOVER, "bottom") || "none",
        paddingHoverLeft: this.getOptionValue(Padding, BaseOption.$states.HOVER, "left") || "none",
        paddingHoverRight: this.getOptionValue(Padding, BaseOption.$states.HOVER, "right") || "none",
        marginHoverTop: this.getOptionValue(Margins, BaseOption.$states.HOVER, "top") || "none",
        marginHoverBottom: this.getOptionValue(Margins, BaseOption.$states.HOVER, "bottom") || "none",
        marginHoverLeft: this.getOptionValue(Margins, BaseOption.$states.HOVER, "left") || "none",
        marginHoverRight: this.getOptionValue(Margins, BaseOption.$states.HOVER, "right") || "none",
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        backgroundColor: this.getOptionValue(BackgroundColor),
        backgroundGradient: this.getOptionValue(BackgroundGradient),
      };
    },
    imageFilterEffects() {
      return {
        blur: this.getOptionValue(Blur),
        brightness: this.getOptionValue(Brightness),
        contrast: this.getOptionValue(Contrast),
        grayscale: this.getOptionValue(Grayscale),
        "hue-rotate": this.getOptionValue(HueRotate),
        invert: this.getOptionValue(Invert),
        opacity: this.getOptionValue(ImageOpacity),
        saturate: this.getOptionValue(Saturate),
        sepia: this.getOptionValue(Sepia),
      };
    },
  },
};
